// client/src/contexts/LocaleContext.js
import React, { createContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Create rtl cache
const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create ltr cache
const ltrCache = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

export const LocaleContext = createContext();

export const LocaleProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState(i18n.language === 'ar' ? 'rtl' : 'ltr');
  
  // Create a theme instance for the current direction
  const theme = createTheme({
    direction: direction,
    palette: {
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
    typography: direction === 'rtl' 
      ? { fontFamily: '"Tajawal", "Roboto", "Helvetica", "Arial", sans-serif' }
      : { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
  });

  useEffect(() => {
    // Update direction when language changes
    const newDirection = i18n.language === 'ar' ? 'rtl' : 'ltr';
    setDirection(newDirection);
    
    // Set document direction
    document.dir = newDirection;
    document.documentElement.lang = i18n.language;
    
    console.log('Language changed to:', i18n.language, 'Direction:', newDirection);
  }, [i18n.language]);

  const changeLanguage = (language) => {
    console.log('Changing language to:', language);
    i18n.changeLanguage(language);
  };

  return (
    <LocaleContext.Provider value={{ direction, changeLanguage }}>
      <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </CacheProvider>
    </LocaleContext.Provider>
  );
};